import React, { useEffect, useContext, useState } from "react";
import { useSelector } from "react-redux";
import { useQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import classNames from "classnames";
import Drawer from "@material-ui/core/Drawer";
import { makeStyles } from "@material-ui/styles";
import Typography from "@material-ui/core/Typography";
import FilterIcon from "@material-ui/icons/FilterList";
import { STATE_CULTURE } from "../Actions/stateActions";
import OccasionsList from "./occationsList";
import Selector from "./selector";
import { SpinnerContainer } from "../Common/UIComponents";
import moment from "moment";
import Filter from "../Filter";
import BusySpinner from "../Common/busySpinner";
import Refetch from "../Common/refetch";
import { AppContext, SET_OCCASIONFILTER } from "../Context";
import { UnstyledButton } from "@mantine/core";

const useStyles = makeStyles((theme) => ({
  weekView: {
    backgroundColor: theme.app.background[0],
    color: theme.app.color,
    // height: `${theme.height - 64}px`,
    position: "relative",
  },
  header: {
    background: theme.app.background[1],
    color: theme.app.color,
    minHeight: "80px",
    height: "fit-content",
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    alignItems: "center",
    borderBottom: `1px solid ${theme.app.borderColor}`,
    padding: "0 16px",
    "@media(max-width: 768px)": {
      padding: 0,
      gridTemplateColumns: "1fr",
    },
  },
  headerText: {
    fontSize: "1.35rem",
    fontWeight: 500,
    color: theme.app.labelColor,
    "@media(max-width: 768px)": {
      display: "none",
    },
  },
  drawerPaper: {
    width: "300px",
    "@media(max-width:667px)": {
      width: "100%",
    },
  },
  filterButtonContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    color: theme.app.labelColor,
    "@media(max-width: 768px)": {
      display: "none",
    },
  },
  filterButtonContainerSmall: {
    display: "none",
    position: "fixed",
    zIndex: "100",
    right: "16px",
    top: "149px",
    background: theme.app.background[2],
    color: theme.app.labelColor,
    border: `1px solid ${theme.app.borderColor}`,
    borderRadius: "50%",
    padding: "2px",
    "@media(max-width: 768px)": {
      display: "block",
    },
  },
  filterButton: {
    padding: "4px",
    borderRadius: "50%",
    color: theme.app.labelColor,
    transition: theme.transitions.create(["background-color"], {
      duration: theme.transitions.duration.short,
      easing: theme.transitions.easing.easeInOut,
    }),
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  contentContainer: {
    // height: "calc(100% - 500px)",
    overflow: "auto",
    position: "relative",
    paddingBottom: "16px",
    "@media(max-width: 667px)": {
      // height: "calc(100% - 500px)",
      paddingBottom: 0,
      overflowY: "scroll",
      "-webkit-overflow-scrolling": "touch",
    },
  },
}));

export const GET_WEEK = gql`
  query week($week: Int, $year: Int) {
    locations {
      id
      name
    }
    week(week: $week, year: $year) {
      prevWeek
      prevYear
      thisWeek
      thisYear
      nextWeek
      nextYear
      from
      until
      occasions {
        id
        at
        duration
        maxParticipants
        maxQueueSlots
        activity {
          id
          name
          infoText
          color
          tags
          location {
            id
            name
          }
          price {
            inclVat
            priceLevelId
            priceLevelName
          }
        }
        resourceAssignments {
          resource {
            id
            name
          }
          role
        }
        canReserve {
          reserve
          enqueue
          reason
          persons
        }
        freeSlots
        queue
      }
    }
    manages {
      id
      name
      email
      civicRegistrationNumber
      photo
    }
  }
`;

// const GET_LOCATION_NAME = gql`
//   query locations {
//     locations {
//       id
//       name
//     }
//   }
// `

const WeekView = (props) => {
  const { match } = props;
  const classes = useStyles();
  const [filterOpen, setFilterOpen] = useState(false);
  const context = useContext(AppContext);
  const { dispatch } = context;
  const [locationData, setLocationData] = useState(undefined);
  const { language, culture, auth } = useSelector((state) => ({
    auth: state.oidc.user !== null && !state.oidc.user.expired,
    culture: state.cookies[STATE_CULTURE],
    language: state.language,
  }));
  const openFilter = () => setFilterOpen(true);
  const closeFilter = () => setFilterOpen(false);
  const params = {
    week: match.params.week,
    year: match.params.year,
    location: match.params.location,
  };
  const { data, loading, error, refetch } = useQuery(GET_WEEK, {
    variables: {
      week: params.week,
      year: params.year,
    },
    pollInterval: 30000,
  });

  if ((!data || !data.week) && auth) {
    refetch();
  }

  useEffect(() => {
    let pathName = `/booking/week/location/${match.params.location}`;
    let isPathMatchingParams = window.location.pathname === pathName;
    let activeFilters = JSON.parse(localStorage.getItem("ActiveFilters"));

    if (locationData?.locationName !== undefined && isPathMatchingParams) {
      if (!activeFilters?.locations.includes(locationData.locationName)) {
        dispatch({
          type: SET_OCCASIONFILTER,
          payload: {
            type: "locations",
            value: locationData.locationName,
            checked: true,
          },
        });
      }
    }
  }, [match.params.location, locationData, dispatch]);

  if (loading) return <div>loading...</div>;

  if (loading && !data.week)
    return (
      <SpinnerContainer>
        <BusySpinner color="#222" />
      </SpinnerContainer>
    );

  if (error) return <h1>{error.message}</h1>;
  const { week, manages } = data;
  const thisWeek = Number(moment().format("YYYY") + moment().week());
  const locale = culture.substring(0, 2).toLowerCase();
  const yearWeek = Number(
    week.thisYear.toString() +
      (week.thisWeek < 10 ? "0" + week.thisWeek : week.thisWeek)
  );
  const now = moment();
  const currentOccasions = week.occasions
    .filter((o) => moment(o.at).isAfter(now))
    .map((o) => {
      return {
        ...o, //Add weekday to occasions
        weekday: moment(o.at).locale(locale).format("dddd"),
      };
    });
  const firstOccasion = week.occasions && week.occasions[0];
  const locationById = data.locations.filter(
    (location) => location.id === params.location
  );
  const locationNameById = locationById[0] && locationById[0].name;

  if (
    params.location !== undefined &&
    locationData?.locationName === undefined
  ) {
    setLocationData({
      ...locationData,
      locationName: locationNameById,
      locationId: match.params.location,
    });
  }

  return (
    <>
      <main
        className={classNames(classes.weekView, "weekview__main-container")}
      >
        <Refetch
          refetch={refetch}
          notAuth={
            firstOccasion
              ? firstOccasion.canReserve.reason === "NotAuthenticated"
              : undefined
          }
          isAuth={auth}
        />
        <div
          className={classNames(classes.header, "weekview__header-container")}
        >
          <Typography variant="h1" className={classes.headerText}>
            {language.week_schedule}
          </Typography>
          <Selector
            week={week}
            loading={loading}
            anyPrevious={yearWeek > thisWeek}
          />
          <div className={classes.filterButtonContainer}>
            <UnstyledButton
              onClick={openFilter}
              className={classes.filterButton}
              aria-label={language.open_filter}
            >
              <FilterIcon />
            </UnstyledButton>
          </div>
          <div className={classes.filterButtonContainerSmall}>
            <UnstyledButton
              onClick={openFilter}
              className={classes.filterButton}
              aria-label={language.open_filter}
            >
              <FilterIcon />
            </UnstyledButton>
          </div>
        </div>

        <div className={classes.contentContainer}>
          <OccasionsList
            occasions={currentOccasions}
            manages={manages}
            week={week}
            loading={loading}
          />
        </div>
      </main>
      <Drawer
        classes={{
          paper: classes.drawerPaper,
        }}
        open={filterOpen}
        onClose={closeFilter}
        anchor="right"
      >
        <Filter week={week} close={closeFilter} />
      </Drawer>
    </>
  );
};

export default WeekView;
